import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Popover, Tooltip } from '@mui/material';
import {
  Home,
  BookOpen,
  Link2,
  ChevronsUpDown,
  ChevronRight,
  Trophy,
  LogOut,
  User,
  ListTodo,
  Compass,
  GraduationCap,
  Award,
  PanelLeftClose,
  PanelRightClose,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useConfig } from '@/hooks/useConfig';
import { useShowNotifications } from '@/hooks';
import { usePendingContent } from '@/hooks';
import { usePendingContentRedirect } from '@/hooks/usePendingContentRedirect';
import { useUser } from '@/components/AuthInterceptorComponent/hooks/useUser';
import { get } from '@/services/api';
import { apiUrls } from '@/constants/apiUrls';
import { QueryKeys } from '@/constants/queryKeys';
import { useSidebarOpen } from '@/context/SidebarContext';
import { sidebarStyles } from './Sidebar.styles';
import { UserPhoto } from '@/pages/HomePage/components/UserPhoto';
import { RightBarIcons } from '@/components/RightBarIcons';
import { LogoutModal } from '@/pages/v2/profile/components/logout-modal';
import { LogoSection } from '@/components/Navbar/components';
import { useMetrics } from '@/hooks/useMetrics';

// ─── SidebarTopBar (mirrors SidebarInset header) ────────────────────────────
export const SidebarTopBar = () => {
  const { classes } = sidebarStyles({});
  const { toggle } = useSidebarOpen();
  const location = useLocation();
  const { t } = useTranslation();
  const { open } = useSidebarOpen();

  const getBreadcrumb = () => {
    const path = location.pathname;
    if (path === '/' || path === '')
      return [{ label: t('general.home'), href: '/', current: true }];
    if (path.startsWith('/content'))
      return [
        { label: t('general.home'), href: '/' },
        {
          label: t('contentPageNew.myCourses'),
          href: '/content',
          current: true,
        },
      ];
    if (path.startsWith('/points'))
      return [
        { label: t('general.home'), href: '/' },
        { label: t('general.points'), href: '/points', current: true },
      ];
    if (path.startsWith('/notifications'))
      return [
        { label: t('general.home'), href: '/' },
        {
          label: t('general.notifications'),
          href: '/notifications',
          current: true,
        },
      ];
    if (path.startsWith('/profile'))
      return [
        { label: t('general.home'), href: '/' },
        { label: t('general.profile'), href: '/profile', current: true },
      ];
    return [{ label: t('general.home'), href: '/', current: true }];
  };

  const crumbs = getBreadcrumb();

  return (
    <div className={classes.topBar}>
      <div className={classes.topBarInner}>
        {/* SidebarTrigger */}
        <button
          className={classes.sidebarTrigger}
          onClick={toggle}
          type="button"
          aria-label="Toggle sidebar"
        >
          {open ? (
            <PanelLeftClose size={18} strokeWidth={1.8} />
          ) : (
            <PanelRightClose size={18} strokeWidth={1.8} />
          )}
        </button>
        {/* Separator */}
        <div className={classes.topBarSeparator} />
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb">
          <ol className={classes.breadcrumbList}>
            {crumbs.map((crumb, i) => (
              <li
                key={crumb.href}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {i > 0 && (
                  <span className={classes.breadcrumbSeparator}>
                    <ChevronRight size={14} strokeWidth={1.8} />
                  </span>
                )}
                {crumb.current ? (
                  <span className={classes.breadcrumbPage}>{crumb.label}</span>
                ) : (
                  <Link to={crumb.href} className={classes.breadcrumbLink}>
                    {crumb.label}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {/* Right side: points pill + bell */}
      <RightBarIcons />
    </div>
  );
};

// ─── Main Sidebar (mirrors AppSidebar) ──────────────────────────────────────
export const Sidebar = () => {
  const { t } = useTranslation();
  const { open } = useSidebarOpen();
  const location = useLocation();
  const navigate = useNavigate();
  const config = useConfig();
  const { data: user } = useUser();
  const { showBell: _showBell } = useShowNotifications();
  const { hasPendingContent, pendings } = usePendingContent();
  const { handlePendingContentRedirect } = usePendingContentRedirect();
  const { classes, cx } = sidebarStyles({ open });
  const { trackEvent } = useMetrics();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const userButtonRef = useRef<HTMLButtonElement>(null);

  const { data: rankingData } = useQuery({
    queryKey: [QueryKeys.RANKING],
    queryFn: async () => {
      const response = await get(apiUrls.users.ranking);
      return response.data;
    },
  });

  const isActive = (path: string) => location.pathname === path;
  const isPendingActive = location.search.includes('party=true');
  const showGamification = config.showGammification !== false;
  const showRanking =
    showGamification && config.showComparativeRanking !== false;
  const companyName = config.name ?? 'HackU';

  const trackNav = (nav_item: string) => {
    trackEvent({
      action: 'nav_item_clicked',
      section: 'home',
      companies: config.slug_name ? [config.slug_name] : undefined,
      extra_data: { nav_item },
    });
  };

  const logout = () => {
    setOpenLogoutModal(true);
    setUserMenuOpen(false);
  };

  const handleLogout = async () => {
    await get('users/logout');
    localStorage.clear();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    window.location.href = `${import.meta.env.VITE_APP_LOGIN_HOST}/logout/`!;
  };

  return (
    <>
      <LogoutModal
        openLogoutModal={openLogoutModal}
        setOpenLogoutModal={setOpenLogoutModal}
        handleLogout={handleLogout}
      />
      <aside className={cx(classes.root, !open && classes.rootCollapsed)}>
        {/* ─── SidebarHeader → TeamSwitcher ───────────────────────────── */}
        <div className={classes.sidebarHeader}>
          <div
            className={cx(
              classes.teamButton,
              !open && classes.teamButtonIconOnly,
            )}
          >
            <div className={classes.teamLogo}>
              <LogoSection
                hideHackuLogo={config.hideHackuLogo}
                customLogoUrl={config?.logo}
              />
            </div>
            {open && (
              <div className={classes.teamInfo}>
                <p className={classes.teamPlan}>{config?.name}</p>
              </div>
            )}
          </div>
        </div>

        {/* ─── SidebarContent ─────────────────────────────────────────── */}
        <div className={classes.sidebarContent}>
          {/* Single group — NAVEGACIÓN */}
          <div
            className={cx(
              classes.sidebarGroup,
              !open && classes.sidebarGroupCollapsed,
            )}
          >
            {open && (
              <div className={classes.groupLabel}>
                {t('sidebar.groupNavigation')}
              </div>
            )}
            <ul className={classes.sidebarMenu}>
              {/* Home */}
              <li>
                <Tooltip
                  title={!open ? t('general.home') : ''}
                  placement="right"
                  arrow
                >
                  <Link
                    to="/"
                    onClick={() => trackNav('inicio')}
                    className={cx(
                      classes.menuButton,
                      !open && classes.menuButtonIconOnly,
                      isActive('/') && classes.menuButtonActive,
                    )}
                  >
                    <Home size={18} strokeWidth={1.8} />
                    {open && <span>{t('general.home')}</span>}
                  </Link>
                </Tooltip>
              </li>

              {/* Contents */}
              <li>
                <Tooltip
                  title={
                    !open
                      ? config.isSimple
                        ? t('homeScreen.myContents')
                        : t(`contentPageNew.myCourses${config.isMeidei}`)
                      : ''
                  }
                  placement="right"
                  arrow
                >
                  <Link
                    to="/content"
                    onClick={() => trackNav('contenidos')}
                    className={cx(
                      classes.menuButton,
                      !open && classes.menuButtonIconOnly,
                      isActive('/content') && classes.menuButtonActive,
                    )}
                  >
                    <BookOpen size={18} strokeWidth={1.8} />
                    {open && (
                      <span>
                        {config.isSimple
                          ? t('homeScreen.myContents')
                          : t(`contentPageNew.myCourses${config.isMeidei}`)}
                      </span>
                    )}
                  </Link>
                </Tooltip>
              </li>

              {/* Certificates */}
              {config.showCertificates && (
                <li>
                  <Tooltip
                    title={!open ? t('general.certificates') : ''}
                    placement="right"
                    arrow
                  >
                    <Link
                      to="/certificate"
                      onClick={() => trackNav('certificados')}
                      className={cx(
                        classes.menuButton,
                        !open && classes.menuButtonIconOnly,
                        isActive('/certificate') && classes.menuButtonActive,
                      )}
                    >
                      <Award size={18} strokeWidth={1.8} />
                      {open && <span>{t('general.certificates')}</span>}
                    </Link>
                  </Tooltip>
                </li>
              )}

              {/* Pensums */}
              <li>
                <Tooltip
                  title={!open ? t('homeScreen.pensums') : ''}
                  placement="right"
                  arrow
                >
                  <Link
                    to="/pensums"
                    onClick={() => trackNav('pensums')}
                    className={cx(
                      classes.menuButton,
                      !open && classes.menuButtonIconOnly,
                      isActive('/pensums') && classes.menuButtonActive,
                    )}
                  >
                    <GraduationCap size={18} strokeWidth={1.8} />
                    {open && <span>{t('homeScreen.pensums')}</span>}
                  </Link>
                </Tooltip>
              </li>

              {/* Available pensums (free pensum selection) */}
              {config.freePensumSelection && (
                <li>
                  <Tooltip
                    title={!open ? t('availablePensums.title') : ''}
                    placement="right"
                    arrow
                  >
                    <Link
                      to="/pensums/available"
                      onClick={() => trackNav('available-pensums')}
                      className={cx(
                        classes.menuButton,
                        !open && classes.menuButtonIconOnly,
                        location.pathname.startsWith('/pensums/available') &&
                          classes.menuButtonActive,
                      )}
                    >
                      <Compass size={18} strokeWidth={1.8} />
                      {open && <span>{t('availablePensums.title')}</span>}
                    </Link>
                  </Tooltip>
                </li>
              )}

              {/* Ranking (gamification) */}
              {showRanking && (
                <li>
                  <Tooltip
                    title={!open ? 'Ranking' : ''}
                    placement="right"
                    arrow
                  >
                    <Link
                      to="/position"
                      onClick={() => trackNav('ranking')}
                      className={cx(
                        classes.menuButton,
                        !open && classes.menuButtonIconOnly,
                        isActive('/position') && classes.menuButtonActive,
                      )}
                    >
                      <Trophy size={18} strokeWidth={1.8} />
                      {open && <span>{t('positionsScreen.title')}</span>}
                    </Link>
                  </Tooltip>
                </li>
              )}

              {Boolean(config.isMeidei) && (
                <li>
                  <Tooltip
                    title={!open ? t('sidebar.myAccess') : ''}
                    placement="right"
                    arrow
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (user?.menu_links?.my_certs) {
                          window.location.href = user.menu_links.my_certs;
                        }
                      }}
                      className={cx(
                        classes.menuButton,
                        !open && classes.menuButtonIconOnly,
                      )}
                    >
                      <Link2 size={18} strokeWidth={1.8} />
                      {open && <span>{t('sidebar.myAccess')}</span>}
                    </div>
                  </Tooltip>
                </li>
              )}

              {/* Pending content */}
              <li>
                <Tooltip
                  title={!open ? t('general.pendings') : ''}
                  placement="right"
                  arrow
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={handlePendingContentRedirect}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ')
                        handlePendingContentRedirect();
                    }}
                    className={cx(
                      classes.menuButton,
                      !open && classes.menuButtonIconOnly,
                      isPendingActive && classes.menuButtonActive,
                    )}
                  >
                    <ListTodo size={18} strokeWidth={1.8} />
                    {open && <span>{t('general.pendings')}</span>}
                    {hasPendingContent && pendings > 0 && (
                      <span className={classes.badge}>
                        {pendings > 99 ? '99+' : pendings}
                      </span>
                    )}
                  </div>
                </Tooltip>
              </li>
            </ul>
          </div>
        </div>

        {/* ─── SidebarFooter → NavUser with dropdown ─────────────────── */}
        <div className={classes.sidebarFooter}>
          <Tooltip
            title={
              !open ? `${user?.first_name ?? ''} ${user?.last_name ?? ''}` : ''
            }
            placement="right"
            arrow
          >
            <button
              ref={userButtonRef}
              className={cx(
                classes.userButton,
                !open && classes.userButtonIconOnly,
              )}
              onClick={() => setUserMenuOpen(true)}
              type="button"
            >
              <UserPhoto size="small" />
              {open && (
                <>
                  <div className={classes.userInfo}>
                    <p className={classes.userName}>{user?.first_name}</p>
                    {showRanking ? (
                      <p className={classes.userSubText}>
                        {companyName} · #{rankingData?.position ?? '-'}
                      </p>
                    ) : (
                      <p className={classes.userSubText}>{companyName}</p>
                    )}
                  </div>
                  <ChevronsUpDown
                    size={18}
                    strokeWidth={1.8}
                    className={classes.chevronsIcon}
                  />
                </>
              )}
            </button>
          </Tooltip>

          {/* User dropdown menu (opens upward to the right like shadcn NavUser) */}
          <Popover
            open={userMenuOpen}
            anchorEl={userButtonRef.current}
            onClose={() => setUserMenuOpen(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{
              paper: {
                className: classes.userDropdown,
              },
            }}
          >
            {/* Header: avatar + name + email */}
            <div className={classes.dropdownHeader}>
              <UserPhoto size="small" />
              <div className={classes.userInfo}>
                <p className={classes.userName}>
                  {user?.first_name} {user?.last_name}
                </p>
                <p className={classes.userEmail}>{user?.email ?? ''}</p>
              </div>
            </div>

            <div className={classes.dropdownSeparator} />

            {/* Menu items */}
            <div className={classes.dropdownGroup}>
              <button
                className={classes.dropdownItem}
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/profile');
                }}
                type="button"
              >
                <User size={18} strokeWidth={1.8} />
                <span>{t('general.profile') || 'Perfil'}</span>
              </button>
            </div>

            <div className={classes.dropdownSeparator} />

            <div className={classes.dropdownGroup}>
              <button
                className={classes.dropdownItem}
                onClick={logout}
                type="button"
              >
                <LogOut size={18} strokeWidth={1.8} />
                <span>{t('general.logout') || 'Cerrar sesión'}</span>
              </button>
            </div>
          </Popover>
        </div>
      </aside>
    </>
  );
};
